/** @jsxImportSource @emotion/react */
import { skipToken } from '@reduxjs/toolkit/query/react'
import { Option, SelectInput } from 'components/atoms/Select'
import {
  billingInsuranceTypeCodeOptions,
  SECONDARY_PAYER_INSURANCE_TYPE_UPAYER_ID,
} from 'components/lab/billingCharge/consts'
import {
  BillingChargeFormValues,
  ChargeSection,
} from 'components/lab/billingCharge/BillingChargeUpsert'
import {
  SubmissionMethodControl,
  SubmissionMethodControlProps,
} from 'components/lab/billingCharge/sections/SubmissionMethodControl'
import { ClaimPayerRank } from 'components/lab/billingCharge/utils'
import { BILLING_LOCATION_CODES } from 'components/lab/constants/payors'
import { billingProviderOptions } from 'components/lab/payer/chargeRules/chargeRule'
import { filterPayerOption, formatPayerOptionLabel } from 'components/lab/payer/PayerOptionLabel'
import { SearchParams } from 'components/lab/order/Order'
import { Field, FormikErrors, FormikTouched } from 'formik'
import { usePlatformMode } from 'hooks/usePlatformMode'
import React, { useEffect } from 'react'
import ReactSelect from 'react-select'
import { useGetLabPayerQuery } from 'store/services/payer'

type PayerOption = {
  value: number | string
  label: string
  uPayerId?: string | null
  matchedName?: string | null
}

const POSITION_LABELS: Record<number, string> = {
  1: 'Primary',
  2: 'Secondary',
  3: 'Tertiary',
}

const positionLabel = (position: number): string => POSITION_LABELS[position] || `Payer ${position}`

const POSITION_RANKS: Record<number, ClaimPayerRank> = {
  1: 'primary',
  2: 'secondary',
  3: 'tertiary',
}

// A plan's label with, beside it, how that payer's claim is sent.
const PlanLabel = ({
  htmlFor,
  children,
  submissionMethod,
}: {
  htmlFor: string
  children: React.ReactNode
  submissionMethod?: SubmissionMethodControlProps
}) => (
  <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
    <label htmlFor={htmlFor}>{children}</label>
    {submissionMethod && <SubmissionMethodControl {...submissionMethod} />}
  </div>
)

interface Props {
  id: string | undefined
  values: BillingChargeFormValues
  setFieldValue: (field: string, value: any) => void
  errors: FormikErrors<BillingChargeFormValues>
  touched: FormikTouched<BillingChargeFormValues>
  changeSearch: (field: string, value: string | boolean) => void
  payersOptions: PayerOption[] | undefined
  physicianOptions: Option[] | undefined
  orderOptions: Option[] | undefined
  search: SearchParams
  isPatientCharge: boolean
  disableAll: boolean
  // Primary claim submitted and not rejected by the payer: primary
  // payer/member/group are locked. A rejected claim unlocks them so it can be
  // corrected and resubmitted (see isPayerIdentityLocked).
  primaryLocked?: boolean
  // The same for the COB (secondary) claim and the secondary payer fields.
  secondaryLocked?: boolean
  // The payers' own Claim Numbers (ICN), from their remittances, by rank.
  payerClaimNumbers?: { rank: string; value: string }[]
  // How each saved payer's claim is sent, keyed by rank. A rank with no payer
  // on the saved charge has no entry, so no control is shown for it.
  submissionMethods?: Partial<Record<ClaimPayerRank, SubmissionMethodControlProps>>
}

export const ChargeInfoSection = ({
  id,
  setFieldValue,
  changeSearch,
  touched,
  payersOptions,
  physicianOptions,
  orderOptions,
  values,
  search,
  errors,
  isPatientCharge,
  disableAll,
  primaryLocked,
  secondaryLocked,
  payerClaimNumbers = [],
  submissionMethods,
}: Props) => {
  const { isIRhythmMode } = usePlatformMode()

  const selectedSecondaryPayerOption = payersOptions?.find(
    (option) => String(option.value) === String(values.secondaryPayerId),
  ) as PayerOption | undefined

  // The chain past the two editable slots, in payment order.
  const downstreamPayers = React.useMemo(
    () =>
      (values.payers ?? [])
        .filter((payer) => Number(payer.position) > 2)
        .sort((a, b) => Number(a.position) - Number(b.position)),
    [values.payers],
  )

  const { data: secondaryPayer, isFetching: isSecondaryPayerFetching } = useGetLabPayerQuery(
    values.secondaryPayerId ? { id: String(values.secondaryPayerId) } : skipToken,
  )

  // A COB claim to Medicare as the tertiary payer needs the code as well.
  const tertiaryPayerId = downstreamPayers.find((payer) => Number(payer.position) === 3)?.payerId
  const { data: tertiaryPayer, isFetching: isTertiaryPayerFetching } = useGetLabPayerQuery(
    tertiaryPayerId ? { id: String(tertiaryPayerId) } : skipToken,
  )

  const shouldShowInsuranceTypeCode =
    selectedSecondaryPayerOption?.uPayerId === SECONDARY_PAYER_INSURANCE_TYPE_UPAYER_ID ||
    secondaryPayer?.uPayerId === SECONDARY_PAYER_INSURANCE_TYPE_UPAYER_ID ||
    tertiaryPayer?.uPayerId === SECONDARY_PAYER_INSURANCE_TYPE_UPAYER_ID

  const shouldDeferInsuranceTypeCodeReset =
    (Boolean(values.secondaryPayerId) &&
      !selectedSecondaryPayerOption?.uPayerId &&
      !secondaryPayer &&
      isSecondaryPayerFetching) ||
    (Boolean(tertiaryPayerId) && !tertiaryPayer && isTertiaryPayerFetching)

  useEffect(() => {
    if (!shouldShowInsuranceTypeCode && !shouldDeferInsuranceTypeCodeReset && values.insuranceTypeCode) {
      setFieldValue('insuranceTypeCode', '')
    }
  }, [
    setFieldValue,
    shouldDeferInsuranceTypeCodeReset,
    shouldShowInsuranceTypeCode,
    values.insuranceTypeCode,
  ])

  return (
    <ChargeSection title={<div>Insurance - Appointment - Provider</div>}>
      <fieldset disabled={disableAll}>
      <div className="container-fluid">
        <div className="row">
          <div className="col">
            <label htmlFor="placeOfService">
              Place of Service <span className="text-danger">*</span>
            </label>
            <Field
              component={SelectInput}
              options={[
                { key: '11', value: '11: Office' },
                { key: '12', value: '12: Home' },
                { key: '81', value: '81: Independent Laboratory' },
              ]}
              name="placeOfService"
            />
            {touched.placeOfService && errors.placeOfService && (
              <div className="text-danger">{errors.placeOfService as string}</div>
            )}
          </div>
          <div className="col">
            <PlanLabel htmlFor="primaryPlan" submissionMethod={submissionMethods?.primary}>
              Primary Plan <span className="text-danger">*</span>
            </PlanLabel>
            <ReactSelect
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 10 }),
              }}
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Primary Plan"
              name="primaryPayerId"
              isDisabled={isPatientCharge || disableAll || primaryLocked}
              onBlur={() => {
                changeSearch('payerName', '')
              }}
              onChange={(item) => {
                setFieldValue('primaryPayerId', item?.value ?? '')
                setFieldValue('primaryPayerName', item?.label ?? '')
              }}
              options={payersOptions}
              formatOptionLabel={formatPayerOptionLabel}
              filterOption={filterPayerOption}
              value={
                payersOptions?.find((o) => o.value === values.primaryPayerId) ||
                (values.primaryPayerId
                  ? {
                      label: values.primaryPayerName,
                      value: values.primaryPayerId,
                    }
                  : null)
              }
              onInputChange={(v) => v && changeSearch('payerName', v)}
            />
            {touched.primaryPayerId && errors.primaryPayerId && (
              <div className="text-danger">{errors.primaryPayerId as string}</div>
            )}
          </div>
          <div className="col">
            <PlanLabel htmlFor="secondaryPlan" submissionMethod={submissionMethods?.secondary}>
              Secondary Plan
            </PlanLabel>
            <ReactSelect
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 10 }),
              }}
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Secondary Plan"
              name="secondaryPayerId"
              isDisabled={isPatientCharge || disableAll || secondaryLocked}
              onBlur={() => {
                changeSearch('payerName', '')
              }}
              onChange={(item) => {
                setFieldValue('secondaryPayerId', item?.value ?? '')
                setFieldValue('secondaryPayerName', item?.label ?? '')
              }}
              options={payersOptions}
              formatOptionLabel={formatPayerOptionLabel}
              filterOption={filterPayerOption}
              value={
                payersOptions?.find((o) => o.value === values.secondaryPayerId) ||
                (values.secondaryPayerId
                  ? {
                      label: values.secondaryPayerName,
                      value: values.secondaryPayerId,
                    }
                  : null)
              }
              onInputChange={(v) => v && changeSearch('payerName', v)}
            />
          </div>
          {/* Payers past the secondary are managed on the order's Payors tab,
              so they are shown here but not editable. */}
          {downstreamPayers.map((payer) => (
            <div className="col" key={payer.position}>
              <PlanLabel
                htmlFor={`payer-${payer.position}`}
                submissionMethod={POSITION_RANKS[Number(payer.position)]
                  ? submissionMethods?.[POSITION_RANKS[Number(payer.position)]]
                  : undefined}>
                {positionLabel(payer.position)} Plan
              </PlanLabel>
              <input
                id={`payer-${payer.position}`}
                type="text"
                className="form-control"
                disabled
                title="Managed on the order's payers, not on the charge"
                value={payer.payerName || `Payer ${payer.payerId}`}
              />
            </div>
          ))}
          <div className="col">
            <label htmlFor="locationCode">
              Location <span className="text-danger">*</span>
            </label>
            <Field component={SelectInput} options={BILLING_LOCATION_CODES} name="locationCode" />
            {touched.locationCode && errors.locationCode && (
              <div className="text-danger">{errors.locationCode as string}</div>
            )}
          </div>
          <div className="col">
            <label htmlFor="claimType">
              Claim Type <span className="text-danger">*</span>
            </label>
            <Field
              component={SelectInput}
              disabled={!id || isPatientCharge || disableAll}
              options={[
                { key: 'ORIGINAL', value: 'Original' },
                { key: 'REPLACEMENT', value: 'Replacement' },
                { key: 'VOID', value: 'Void' },
              ]}
              name="claimType"
            />
            {touched.claimType && errors.claimType && (
              <div className="text-danger">{errors.claimType as string}</div>
            )}
          </div>
        </div>
        <div className="row mb-3">
          <div className="col">
            <label htmlFor="primaryMemberId">
              Primary Payer Member ID<span className="text-danger">*</span>
            </label>
            <Field
              type="text"
              name="primaryMemberId"
              className="form-control"
              placeholder="Primary Payer Member ID"
              disabled={isPatientCharge || disableAll || primaryLocked}
            />
            {touched.primaryMemberId && errors.primaryMemberId && (
              <div className="text-danger">{errors.primaryMemberId as string}</div>
            )}
          </div>
          <div className="col">
            <label htmlFor="primaryGroupId">Primary Payer Group ID</label>
            <Field
              type="text"
              name="primaryGroupId"
              className="form-control"
              placeholder="Primary Payer Group ID"
              disabled={isPatientCharge || disableAll || primaryLocked}
            />
          </div>
          <div className="col">
            <label htmlFor="secondaryMemberId">
              Secondary Payer Member ID
            </label>
            <Field
              type="text"
              name="secondaryMemberId"
              className="form-control"
              placeholder="Secondary Payer Member ID"
              disabled={!values.secondaryPayerId || secondaryLocked}
            />
          </div>
          <div className="col">
            <label htmlFor="secondaryPayerGroupId">Secondary Payer Group ID</label>
            <Field
              type="text"
              name="secondaryGroupId"
              className="form-control"
              placeholder="Secondary Payer Group ID"
              disabled={!values.secondaryPayerId || secondaryLocked}
            />
          </div>
          {shouldShowInsuranceTypeCode && (
            <div className="col">
              <label htmlFor="insuranceTypeCode">Insurance Type Code</label>
              <Field
                component={SelectInput}
                options={billingInsuranceTypeCodeOptions}
                name="insuranceTypeCode"
                disabled={disableAll}
              />
            </div>
          )}
        </div>
        <div className="row">
          <div className="col">
            <label htmlFor="orderId">Order</label>
            <ReactSelect
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 10 }),
              }}
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Order"
              name="orderId"
              isDisabled={disableAll}
              options={orderOptions}
              value={
                orderOptions?.find((option) => String(option.value) === String(values.orderId)) ||
                (values.orderId
                  ? {
                      label: `#${values.orderId}`,
                      value: values.orderId,
                    }
                  : null)
              }
              onChange={(item) => {
                setFieldValue('orderId', item?.value ?? null)
              }}
            />
          </div>
          <div className="col">
            <label htmlFor="renderingAndBilling">
              Rendering / Billing <span className="text-danger">*</span>
            </label>
            <Field
              component={SelectInput}
              options={billingProviderOptions(isIRhythmMode, values.renderingAndBilling)}
              name="renderingAndBilling"
            />
            {touched.renderingAndBilling && errors.renderingAndBilling && (
              <div className="text-danger">{errors.renderingAndBilling as string}</div>
            )}
          </div>
          <div className="col">
            <label htmlFor="originalRef">Original Ref #</label>
            <Field
              type="text"
              name="originalRef"
              className="form-control"
              // Left blank, a corrected or void claim cites the payer's own
              // Claim Number for the rank it goes to.
              placeholder={payerClaimNumbers[0] ? `Payer's Claim Number (${payerClaimNumbers[0].value})` : 'Original Ref #'}
              disabled={!id || isPatientCharge || disableAll}
            />
            {payerClaimNumbers.length > 0 && (
              <small className="text-muted d-block mt-1">
                Claim Number:{' '}
                {payerClaimNumbers.map(({ rank, value }) => `${value} (${rank})`).join(' · ')}
              </small>
            )}
          </div>
          <div className="col">
            <label htmlFor="pan">PAN</label>
            <Field type="text" name="pan" className="form-control" placeholder="PAN" />
          </div>
          <div className="col">
            <label htmlFor="referringPhysicianNpi">Referring Physician</label>
            <ReactSelect
              menuPortalTarget={document.body}
              styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 10,
              }),
            }}
            className="basic-single"
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder="Select Physician"
            name="referringPhysicianNpi"
            isDisabled={isPatientCharge || disableAll}
            onChange={(item) => {
              setFieldValue('referringPhysicianNpi', item?.value ?? '')
              setFieldValue('referringPhysicianName', item?.label ?? '')
            }}
              options={physicianOptions}
              value={
                physicianOptions?.find((o) => o.value === values.referringPhysicianNpi) ||
                (values.referringPhysicianNpi
                  ? {
                      label: values.referringPhysicianName,
                      value: values.referringPhysicianNpi,
                    }
                  : null)
              }
              onInputChange={(v) => changeSearch('physicianName', v)}
              inputValue={search.physicianName}
            />
          </div>
        </div>
      </div>
      </fieldset>
    </ChargeSection>
  )
}
